import { useEffect } from 'react';
import { useTranslate } from '../../hooks/useTranslate';
import { useCEProductSelector } from '../../bitf-customization-engine/useCEProductSelector';
import CEProductCollectionSelector from './CEProductCollectionSelector';
import { useCEProductCustomization } from '../../bitf-customization-engine/useCEProductCustomization';

export default function ProductSelector({ productSelectorHook, localization, storeName = 'quick-buy' }) {
  const { translate } = useTranslate(localization?.locale);
  const {
    collections,
    filteredFilters,
    selectedCollection,
    selectedProduct,
    selectedVariant,
    selectedFilters,
    setCollection,
    setFilterValue,
    setVariant,
  } = productSelectorHook || useCEProductSelector({ localization });

  const { setStore } = useCEProductCustomization({
    storeName: storeName,
    watchStoreChanges: false,
    localization,
  });

  const sizeLabelList = 'size,taille,größe,tamanho,tamaño,koko,サイズ,taglia,size_id'.split(',');

  useEffect(() => {
    if (selectedProduct) {
      setStore((store) => {
        if (store.currentProduct?.id === selectedProduct.id) return;
        store.currentProduct = selectedProduct;
      });
      // Mantieni la taglia precedentemente selezionata, se possibile (case-insensitive sui nomi opzione)
      const prevSize = selectedVariant?.selectedOptions?.find((opt) => {
        const optName = (opt?.name || '').toLowerCase();
        return sizeLabelList.includes(optName);
      })?.value;

      // Trova una variante con la stessa taglia nel nuovo prodotto
      let nextVariant = selectedProduct?.variants?.find((variant) => {
        if (!prevSize) return false;
        return variant?.selectedOptions?.some((opt) => {
          const optName = (opt?.name || '').toLowerCase();
          return sizeLabelList.includes(optName) && opt.value === prevSize;
        });
      });

      // Se la variante con la stessa taglia non è disponibile, prendi la prima disponibile
      if (!nextVariant || !nextVariant.availableForSale) {
        nextVariant = selectedProduct?.variants?.find((v) => v.availableForSale) || selectedProduct?.variants?.[0];
      }

      if (nextVariant) {
        setVariant(nextVariant);
        setStore((store) => {
          store.currentProductVariant = nextVariant;
        });
      }
    }
  }, [selectedProduct, storeName, setStore]);

  return (
    <div className="product-customization__form-container">
      {/* Filters */}
      {selectedCollection &&
        filteredFilters &&
        filteredFilters.map((filter) => (
          <div key={filter.id}>
            <div className="product-customization__form-section-title">
              <p className="Body Medium Uppercase">{filter.label}</p>
            </div>
            <div className="product-customization__form-buttons">
              {filter?.availableValues?.map(({ id, label, disabled }) => (
                <button
                  key={id}
                  className={`customization-button ${selectedFilters[filter.id] === id && !disabled ? 'active Medium' : ''}`}
                  onClick={() => setFilterValue(filter.id, id)}
                  disabled={disabled}
                >
                  <div>{label}</div>
                </button>
              ))}
            </div>
          </div>
        ))}
      {/* Product Options (Variants) */}
      {selectedProduct &&
        (() => {
          const optionNames = Array.from(
            new Set(selectedProduct?.variants?.flatMap((variant) => variant.selectedOptions.map((opt) => opt.name))),
          );

          const optionsByName = {};
          selectedProduct?.variants?.forEach((variant) => {
            variant?.selectedOptions.forEach((opt) => {
              if (!optionsByName[opt.name]) {
                optionsByName[opt.name] = new Set();
              }
              optionsByName[opt.name].add(opt.value);
            });
          });

          return optionNames.map((optionName) => {
            const values = Array.from(optionsByName[optionName]);

            return (
              <div key={optionName}>
                <div className="product-customization__form-section-title">
                  <p className="Body Medium Uppercase">{optionName}</p>
                  {sizeLabelList.includes(optionName.toLowerCase()) ? (
                    selectedProduct?.sizeGuideLink?.value ? (
                      <p className="">
                        {' '}
                        <a
                          id="size-guide-open"
                          href={selectedProduct?.sizeGuideLink?.value}
                          target="_blank"
                          rel="noopener"
                          class="ASRomaFont Body Regular asroma-button-link button-primary"
                        >
                          {translate('customization.form.size_guide')}
                        </a>
                      </p>
                    ) : null
                  ) : null}
                </div>
                <div className="product-customization__form-buttons">
                  {values.map((value) => {
                    const matchingVariant = selectedProduct.variants?.find((variant) => {
                      const hasCurrentValue = variant?.selectedOptions.some(
                        (opt) => opt.name === optionName && opt.value === value,
                      );

                      if (!hasCurrentValue) return false;
                      if (!selectedVariant) return true;

                      return variant?.selectedOptions.every((opt) => {
                        if (opt.name === optionName) {
                          return true;
                        }
                        const selectedOpt = selectedVariant?.selectedOptions?.find((so) => so.name === opt.name);
                        return !selectedOpt || selectedOpt.value === opt.value;
                      });
                    });

                    const isSelected = selectedVariant?.selectedOptions.some(
                      (opt) => opt.name === optionName && opt.value === value,
                    );

                    return (
                      <button
                        key={value}
                        className={`customization-button ${isSelected && matchingVariant?.availableForSale ? 'active Medium' : ''}`}
                        onClick={() => {
                          if (matchingVariant) {
                            setVariant(matchingVariant);
                            setStore((store) => {
                              store.currentProductVariant = matchingVariant;
                            });
                          }
                        }}
                        disabled={!matchingVariant?.availableForSale}
                      >
                        <div>{value}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          });
        })()}
    </div>
  );
}
